/**
 * React hook for managing the hard age verification cookie for One Trust tracking.
 *
 * This hook sets or removes a cookie indicating the user's age verification status,
 * based on the provided age verification method and cookie TTL (time-to-live).
 * The cookie is set only if the user is logged in and has been age verified.
 * If the cookie is already set, it will be removed to ensure correct tracking.
 *
 * @param avMethod - The age verification method used (e.g., diia_async, portone_async).
 * @param cookieTtl - The time-to-live for the cookie in seconds.
 */
import { useEffect } from "react";

import { useUser } from "@ui/hooks/useUser";

import { getCookie, getIsAgeVerificationNotExpired, getUTCDate, removeCookie, setCookie } from "@jti/fe-common";

import type { AGE_VERIFICATION_METHOD } from "../../HardAgeVerificationPopup.types";

type UseHardAgeVerificationCookieData = {
  avMethod: AGE_VERIFICATION_METHOD;
  cookieTtlInSeconds: number;
};

const DIIA_AV_COOKIE_NAME = "isDiiaAgeVerified";
const PASS_AV_COOKIE_NAME = "isPassAgeVerified";

const cookiesNames: Record<AGE_VERIFICATION_METHOD, string> = {
  diia_async: DIIA_AV_COOKIE_NAME,
  portone_async: PASS_AV_COOKIE_NAME,
};

export const saveUserAgeVerificationCookie = (avMethod: AGE_VERIFICATION_METHOD, ttlInSeconds: number) => {
  setCookie(cookiesNames[avMethod], true, "/", getUTCDate(ttlInSeconds * 1000));
};

const removeUserAgeVerificationCookie = (avMethod: AGE_VERIFICATION_METHOD) => {
  removeCookie(cookiesNames[avMethod], "/");
};

export const useHardAgeVerificationCookie = ({ avMethod, cookieTtlInSeconds }: UseHardAgeVerificationCookieData) => {
  const { isUserLoggedIn, user } = useUser();
  const isAgeVerificationNotExpired = getIsAgeVerificationNotExpired();
  const isUserAgeVerified = isUserLoggedIn && user?.is_age_verified;

  useEffect(() => {
    const isCookieSet = getCookie(cookiesNames[avMethod]);
    if (isUserAgeVerified && !isCookieSet) {
      saveUserAgeVerificationCookie(avMethod, cookieTtlInSeconds);
    }

    if (!isUserAgeVerified && isCookieSet && !isAgeVerificationNotExpired) {
      removeUserAgeVerificationCookie(avMethod);
    }
  }, [avMethod, cookieTtlInSeconds, isAgeVerificationNotExpired, isUserAgeVerified]);
};
